import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ClipboardList } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BrandLockup, PoweredByAdmissionHall } from "@/components/Brand";

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Delhi", "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jammu & Kashmir",
  "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra",
  "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Puducherry",
  "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
  "Uttarakhand", "Uttar Pradesh", "West Bengal", "Andaman & Nicobar Islands",
  "Chandigarh", "Dadra & Nagar Haveli and Daman & Diu", "Ladakh", "Lakshadweep",
];

const Field = ({ label, testId, hint, children }) => (
  <label className="block">
    <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">{label}</span>
    <div className="mt-1" data-testid={testId}>{children}</div>
    {hint && <p className="mt-1 text-[11px] text-slate-400">{hint}</p>}
  </label>
);

const inputCls = "h-11 w-full border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A365D]";

export default function Onboarding() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();

  // Pre-fill any fields the user already has so a mid-way refresh doesn't lose progress.
  const [email, setEmail] = useState(user?.email || "");
  const [state, setState] = useState(user?.state || "");
  const [neetScore, setNeetScore] = useState(user?.neet_score ? String(user.neet_score) : "");
  const [busy, setBusy] = useState(false);

  const phoneDisplay = useMemo(() => {
    const p = String(user?.phone || "");
    if (!p) return "";
    // Show as +91 98765 43210 when we can, else raw.
    if (p.length === 12 && p.startsWith("91")) return `+91 ${p.slice(2, 7)} ${p.slice(7)}`;
    return `+${p}`;
  }, [user?.phone]);

  const canSubmit = /\S+@\S+\.\S+/.test(email) && !!state && /^\d+$/.test(neetScore);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/onboarding", {
        email,
        state,
        neet_score: Number(neetScore),
      });
      await refresh();
      toast.success(`Welcome aboard${data.name ? `, ${data.name.split(" ")[0]}` : ""}`);
      nav("/app", { replace: true });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-[1fr_1.1fr]">
      <div className="hidden flex-col justify-between bg-[#1A365D] p-12 text-white lg:flex">
        <BrandLockup size="lg" tone="light" wordmark="always" />
        <div>
          <h2 className="font-display text-4xl font-bold leading-tight tracking-tight">
            Just three quick answers to personalise your dashboard.
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-blue-100">
            We use these to pre-filter allotments, suggest probability tiers, and reach you when a
            new round drops.
          </p>
        </div>
        <div className="space-y-1">
          <p className="text-xs uppercase tracking-widest text-blue-200">Step 2 of 2 · Setup your profile</p>
          <PoweredByAdmissionHall tone="light" />
        </div>
      </div>

      <div className="flex items-center justify-center bg-[#FAFAFA] px-6 py-16">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="onboarding-form">
          <button
            type="button"
            onClick={async () => { await logout(); nav("/login", { replace: true }); }}
            data-testid="onboarding-back-btn"
            className="mb-8 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-slate-500 hover:text-[#1A365D]"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Not you? Sign out
          </button>

          <div className="flex items-center gap-2 text-[#1A365D]">
            <ClipboardList className="h-5 w-5" />
            <span className="font-display text-2xl font-extrabold tracking-tighter">Finish setup</span>
          </div>
          <p className="mt-2 text-sm text-slate-500">
            Your mobile number is already verified. Add these details to unlock your personalised
            dashboard.
          </p>

          <div className="mt-7 space-y-4">
            <Field label="Mobile number (verified)" testId="onboarding-phone-wrap">
              <input
                data-testid="onboarding-phone-input"
                value={phoneDisplay}
                disabled
                readOnly
                className={`${inputCls} cursor-not-allowed bg-slate-50 text-slate-500`}
              />
            </Field>

            <Field label="Email address" testId="onboarding-email-wrap">
              <input
                data-testid="onboarding-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className={inputCls}
              />
            </Field>

            <Field label="State" testId="onboarding-state-wrap">
              <select
                data-testid="onboarding-state-select"
                value={state}
                onChange={(e) => setState(e.target.value)}
                required
                className={inputCls}
              >
                <option value="">Select your state</option>
                {INDIAN_STATES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </Field>

            <Field
              label="NEET score"
              testId="onboarding-score-wrap"
              hint="Enter your total NEET score (out of 720 for UG, out of 800 for PG)."
            >
              <input
                data-testid="onboarding-score-input"
                type="number"
                inputMode="numeric"
                min={0}
                max={800}
                value={neetScore}
                onChange={(e) => setNeetScore(e.target.value.replace(/\D/g, ""))}
                placeholder="e.g. 620"
                required
                className={`${inputCls} font-mono-num`}
              />
            </Field>

            <button
              data-testid="onboarding-submit-btn"
              type="submit"
              disabled={busy || !canSubmit}
              className="flex h-11 w-full items-center justify-center bg-[#1A365D] text-sm font-semibold text-white transition-colors hover:bg-[#122845] disabled:opacity-40"
            >
              {busy ? "Saving…" : "Finish & open dashboard"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
